import React, { useEffect, useState } from "react";
import {
  User,
  Calendar,
  Pill,
  FlaskConical,
  FileText,
  UploadCloud,
  CalendarCheck,
  Eye,
} from "lucide-react";
import axios from "axios";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import {API_URL,FAST_API_URL} from "../api"

export default function PrescriptionPage() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
 const navigate = useNavigate();

  // 🔄 FETCH PRESCRIPTIONS
  const fetchPrescriptions = async () => {
     let headersList = {
      Accept: "*/*",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "application/json",
    };

    const res = await axios.post(
      `${API_URL}/prescriptions`,
        { patient_id: localStorage.getItem("patient_id") }, { headers: headersList }
    );
    const data = await res.data.data;
    setPrescriptions(data);
  };

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  // 📤 UPLOAD PRESCRIPTION
 const handleUpload = async (files) => {
  let headersList = {
    Accept: "*/*",
    Authorization: `Bearer ${localStorage.getItem("token")}`
  };

  const formData = new FormData();

  // 🔥 ADD MULTIPLE FILES
  Array.from(files).forEach((file) => {
    formData.append("files", file);
  });

  setLoading(true);

  try {
    await fetch(`${API_URL}/patient_upload_prescription`, {
      method: "POST",
      body: formData,
      headers: headersList
    });

    fetchPrescriptions();

  } catch (err) {
    console.error(err);
    alert("Upload failed");
  } finally {
    setLoading(false);
  }
};

const generate = async (id) => {
  try {
    setPlanLoading(true);

    const res = await fetch(`${API_URL}/generate_daily_plan`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ id }),
    });

    // ❗ Handle HTTP errors
    if (!res.ok) {
      const text = await res.text(); // sometimes not JSON
      throw new Error(`Server Error: ${res.status} - ${text}`);
    }

    const data = await res.json();

    navigate("/plan", { state: data });

  } catch (err) {
    console.error("FULL ERROR:", err);

    // ✅ Better error messages
    if (err.message.includes("Failed to fetch")) {
       alert("🌐 Network issue. Please try again.");
    } else if (err.message.includes("401")) {
      alert("🔐 Session expired. Please login again.");
    } else {
      alert(`❌ ${err.message}`);
    }

  } finally {
    setPlanLoading(false);
  }
};
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

    <div className="p-6 bg-gray-100 min-h-screen">
      {/* 🔼 Upload Section */}
      <div className="bg-white rounded-xl p-6 mb-6 shadow border border-dashed border-blue-300 text-center">
        <UploadCloud className="mx-auto text-blue-500 mb-2" size={40} />
        <p className="text-gray-600">
          Upload Prescription (PDF, JPG, PNG)
        </p>

        <input
  type="file"
  multiple   // 🔥 IMPORTANT
  accept=".pdf,.jpg,.jpeg,.png"
  className="mt-3"
  onChange={(e) => handleUpload(e.target.files)}
/>

        {loading && <p className="text-blue-500 mt-2">Processing...</p>}
      </div>

      {/* 📋 LIST */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {prescriptions.map((item) => (
          <div
            key={item.id}
            className="bg-white p-4 rounded-xl shadow flex justify-between items-center"
          >
            <div className="flex gap-3 items-center">
              {/* 🖼 IMAGE */}
              {item.image && (
                <img
                  src={`${item.image}`}
                  className="w-16 h-16 rounded-lg object-cover border"
                />
              )}

              <div>
                <p className="font-semibold">{item.date || "N/A"}</p>
                <p className="text-sm text-gray-500">
                  {item.symptoms || "No symptoms"}
                </p>
                <p className="text-xs text-gray-400">
                  {item.medicines?.length || 0} Medicines
                </p>
              </div>
            </div>

            <button
              onClick={() => setSelected(item)}
              className="flex items-center gap-1 px-3 py-1 border rounded-lg hover:bg-gray-100"
            >
              <Eye size={14} /> View
            </button>
          </div>
        ))}
      </div>

      {/* 📄 DETAILS VIEW */}
      {selected && (
        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-gray-700">
              <User size={18} />
              <span>Patient: {selected.patientName}</span>
            </div>

            <div className="flex items-center gap-2 text-gray-500">
              <Calendar size={18} />
              <span>
                {new Date(selected.date).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Symptoms + Diagnosis */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-blue-600 font-semibold">💊 Symptoms</h3>
              <p> {selected.symptoms || "No symptoms"}</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-blue-600 font-semibold">🧠 Diagnosis</h3>
              <p>{selected.diagnosis}</p>
            </div>
          </div>

           {/* Medicines */}
          <div className="mb-4">
  <h3 className="text-blue-600 font-semibold mb-2 flex items-center gap-2">
    💊 Medicines
  </h3>

  <div className="overflow-hidden rounded-lg border">
    <table className="w-full text-sm">
      <thead className="bg-blue-50 text-gray-700">
        <tr>
          <th className="p-2 text-left">Medicine</th>
          <th className="p-2 text-left">Dosage</th>
          <th className="p-2 text-left">Frequency</th>
          <th className="p-2 text-left">Duration</th>
          <th className="p-2 text-left">Timings</th>
        </tr>
      </thead>

      <tbody>
        {selected.medicines?.map((med, index) => (
          <tr key={index} className="border-t">
            {/* Name */}
            <td className="p-2 font-medium">
              {med.medicine_name}
            </td>

            {/* Dosage */}
            <td className="p-2">
              {med.dosage}
            </td>

            {/* Frequency */}
            <td className="p-2">
              {med.frequency} times/day
            </td>

            {/* Duration */}
            <td className="p-2">
              {med.duration_value} {med.duration_unit?.[0]}
            </td>

            {/* Reminder Times */}
            <td className="p-2">
              <div className="flex flex-wrap gap-1">
                {med.reminder_times?.map((time, i) => (
                  <span
                    key={i}
                    className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs"
                  >
                    {time}
                  </span>
                ))}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
</div>

          {/* Lab Tests */}
          {/* <div className="mb-4">
            <h3 className="text-blue-600 font-semibold flex gap-2 items-center">
              <FlaskConical size={16} /> Lab Tests
            </h3>
            <div className="mt-2">
              {selected.labTests?.map((test, i) => (
                <span
                  key={i}
                  className="bg-gray-200 px-3 py-1 rounded-full text-sm mr-2"
                >
                  {test}
                </span>
              ))}
            </div>
          </div> */}

          {/* Notes */}
          <div className="mb-4">
            <h3 className="text-blue-600 font-semibold flex gap-2 items-center">
              <FileText size={16} /> Notes
            </h3>
            <div className="bg-gray-50 p-3 rounded-lg mt-2">
              {selected.notes || "No additional notes."}
            </div>
          </div>

          {/* Daily Plan */}
          <div className="text-right">
            <button  onClick={() => generate(selected.id)} className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700">
              <CalendarCheck size={16} />
              {planLoading ? "Generating Plan..." : "Generate Daily Plan"}
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}